/**
 * The stored versions of a group read as numbers, and as the short line a KPI tile draws.
 *
 * `rawValue` and `versionMethod` moved here from the hub view unchanged: the tile's sparkline
 * needs them too, and two copies of «which number is the default method's» would drift the day an
 * API sends one more field.
 *
 * ## Why the line stops where it does
 *
 * A sparkline has no room to draw a gap, so it cannot do what the metrics chart does at a change
 * of ruler (`metrics-series.ts`). It is cut instead: it starts at the oldest version that still
 * measures the same thing as the one on screen. A line running across a new engine or a new
 * configuration would draw a change of instrument as progress, and a tile is exactly where nobody
 * stops to read why.
 *
 * ## The points the answer key moved
 *
 * A point written because the answer key moved (`measuresTheData`) is on the line: it is measured
 * against the answers the next run will be, so it is that run's «before». But it records the data
 * changing, not the engine improving, so each point says which of the two it is and the line draws
 * those apart.
 */
import type { MethodDelta, MetricsSnapshot } from '../model.js';
import { FALSE_POSITIVES_ALL_VIDEOS, LIES_DETECTED_ALL_VIDEOS } from './metric-explanations.js';
import { FLAT_FIELDS_METHOD, measuresTheData } from './metrics-series.js';

/** The raw number of a metric inside a method block, or null. */
export function rawValue(delta: MethodDelta | undefined, metricKey: string): number | null {
  if (!delta) return null;

  if (metricKey === 'incoherences_detected_pct') return delta.incoherencesDetectedPct;
  if (metricKey === 'false_positives_pct') return delta.falsePositivesPct;
  if (metricKey === LIES_DETECTED_ALL_VIDEOS) return delta.incoherencesDetectedAllVideosPct ?? null;
  if (metricKey === FALSE_POSITIVES_ALL_VIDEOS) return delta.falsePositivesAllVideosPct ?? null;

  return delta.correctPct;
}

/**
 * One method of a stored version.
 *
 * An API older than the per-method measurements sends only the sum, flat; the other method is
 * then absent rather than shown as a row of «—».
 */
export function versionMethod(
  snapshot: MetricsSnapshot,
  methodKey: string,
): MethodDelta | undefined {
  const delta = snapshot.methods[methodKey];
  if (delta) return delta;

  return methodKey === FLAT_FIELDS_METHOD
    ? {
        incoherencesDetectedPct: snapshot.incoherencesDetectedPct,
        falsePositivesPct: snapshot.falsePositivesPct,
        correctPct: snapshot.correctPct,
      }
    : undefined;
}

/**
 * How many versions a tile's line reaches back.
 *
 * Enough to show a direction, few enough that each point is still wider than the stroke on a
 * tile a third of the grid wide. The whole history is the group screen's chart, not this.
 */
export const VERSION_SERIES_LIMIT = 12;

/** Whether two neighbouring versions were measured with a different ruler. */
function rulerChanged(newer: MetricsSnapshot, older: MetricsSnapshot): boolean {
  // Upstream's own flag first: it knows about changes this side cannot see.
  if (newer.comparableWithPrevious === false) return true;

  // A missing key says nothing either way, so only two keys that disagree cut the line.
  return (
    newer.seriesKey !== null && older.seriesKey !== null && newer.seriesKey !== older.seriesKey
  );
}

/** One point of a line: its figure, and whether it records the answer key moving. */
export interface SeriesPoint {
  value: number;
  measuresTheData: boolean;
}

/**
 * One metric of one method across the versions up to the one on screen, oldest first.
 *
 * It ends at `untilSnapshotId`, never at the newest version: the tile shows the version somebody
 * picked, and a line running past it would put a later point beside an older number. Versions
 * with no value for the metric are skipped rather than drawn as zero, and a single point is not a
 * line, so fewer than two values give an empty series.
 *
 * `drawn` picks the versions that are points, and the limit counts those. The others still say
 * whether the line carries on across them: a relabel only a manual measurement recorded cuts the
 * line of a card that does not draw manual measurements.
 */
export function versionSeriesPoints(
  versionsNewestFirst: readonly MetricsSnapshot[],
  methodKey: string,
  metricKey: string,
  untilSnapshotId: string | null,
  limit = VERSION_SERIES_LIMIT,
  drawn: (version: MetricsSnapshot) => boolean = () => true,
): SeriesPoint[] {
  if (untilSnapshotId === null || limit < 1) return [];

  const start = versionsNewestFirst.findIndex((version) => version.snapshotId === untilSnapshotId);
  if (start === -1) return [];

  let newer = versionsNewestFirst[start] as MetricsSnapshot;
  const taken: MetricsSnapshot[] = drawn(newer) ? [newer] : [];

  for (let index = start + 1; index < versionsNewestFirst.length; index += 1) {
    if (taken.length >= limit) break;

    const older = versionsNewestFirst[index] as MetricsSnapshot;

    if (rulerChanged(newer, older)) break;

    if (drawn(older)) taken.push(older);
    newer = older;
  }

  const points = taken
    .map((version) => ({
      value: rawValue(versionMethod(version, methodKey), metricKey),
      measuresTheData: measuresTheData(version.trigger),
    }))
    // Null means «not measured», and an API older than a field sends nothing at all; neither is
    // a point. A non-finite number would bend the whole line, so it goes too.
    .filter(
      (point): point is SeriesPoint =>
        typeof point.value === 'number' && Number.isFinite(point.value),
    )
    .reverse();

  return points.length >= 2 ? points : [];
}

/** `versionSeriesPoints`, as the figures alone. */
export function versionSeries(
  versionsNewestFirst: readonly MetricsSnapshot[],
  methodKey: string,
  metricKey: string,
  untilSnapshotId: string | null,
  limit = VERSION_SERIES_LIMIT,
  drawn?: (version: MetricsSnapshot) => boolean,
): number[] {
  return versionSeriesPoints(
    versionsNewestFirst,
    methodKey,
    metricKey,
    untilSnapshotId,
    limit,
    drawn,
  ).map((point) => point.value);
}

/** Where on the line the points that record the answer key moving are, for it to draw them apart. */
export function dataPointIndices(points: readonly SeriesPoint[]): number[] {
  return points.flatMap((point, index) => (point.measuresTheData ? [index] : []));
}
